import { faFileContract } from '@fortawesome/pro-solid-svg-icons'
import { AnimatePresence } from 'motion/react'
import type { ReactElement } from 'react'
import { useContext, useMemo } from 'react'
import { NoResult } from '~/components/Global/Elements/NoResult/NoResult'
import { Shimmer } from '~/components/Global/Elements/Shimmer/Shimmer'
import { Alert, AlertVariation } from '~/components/Global/Interactions/Alert'
import {
  Button,
  ButtonVariation
} from '~/components/Global/Interactions/Button'
import { ExperienceCard } from '~/domains/applicants/components/ExperienceCard/ExperienceCard'
import { useApplicantExperiences } from '~/domains/applicants/queries/applicantExperiences'
import { useConfig } from '~/domains/configuration/queries/config'
import { useDashboardContext } from '~/domains/dashboard/context/DashboardContext'
import type { DashboardBlockLayoutProps } from '~/domains/dashboard/types'
import { ApplicantModalContext } from '~/utils/contextes'
import { DashboardBlockLayout } from '../../DashboardBlock/DashboardBlockLayout'
import { experiencesList } from './ApplicantExperiencesBlock.css'
import { ApplicantExperiencesConfigDropdown } from './ApplicantExperiencesConfigDropdown'

export const ApplicantExperiencesBlock = (
  props: DashboardBlockLayoutProps
): ReactElement => {
  const { userIds, isUserIdsLoading } = useDashboardContext()
  const { setApplicantCurrentId, setApplicantCollection } = useContext(
    ApplicantModalContext
  )
  const { data: endedSinceDays } = useConfig(
    'dashboard.applicant_experiences_ended_since'
  )

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError
  } = useApplicantExperiences(userIds, endedSinceDays ?? 7, 10)

  const allExperiences = useMemo(() => {
    if (!data?.pages) return []
    return data.pages.flatMap((page) => page['hydra:member'])
  }, [data])

  const applicantCollection = useMemo(
    () =>
      allExperiences
        .filter((experience) => experience?.applicant?.id)
        .map((experience) => ({
          id: experience.applicant.id,
          firstname: experience.applicant.firstname,
          lastname: experience.applicant.lastname
        })),
    [allExperiences]
  )

  const handleLoadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }

  const actions = (
    <div data-onboarding="applicant-experiences-config">
      {endedSinceDays !== undefined && <ApplicantExperiencesConfigDropdown />}
    </div>
  )

  if (isError) {
    return (
      <DashboardBlockLayout {...props}>
        <Alert
          variation={AlertVariation.Danger}
          message="Erreur lors du chargement"
          onDismiss={false}
        />
      </DashboardBlockLayout>
    )
  }

  if (isLoading || isUserIdsLoading) {
    return (
      <DashboardBlockLayout {...props} actions={actions}>
        <div className={experiencesList}>
          <Shimmer height={50} />
          <Shimmer height={50} />
          <Shimmer height={50} />
        </div>
      </DashboardBlockLayout>
    )
  }

  if (allExperiences.length === 0) {
    return (
      <DashboardBlockLayout {...props} actions={actions}>
        <NoResult
          icon={faFileContract}
          message="Aucun contrat à renouveler"
          isSmall
        />
      </DashboardBlockLayout>
    )
  }

  return (
    <DashboardBlockLayout {...props} actions={actions}>
      <AnimatePresence>
        <div className={experiencesList}>
          {allExperiences
            .filter((experience) => experience?.id)
            .map((experience) => (
              <ExperienceCard
                key={experience.id}
                experience={experience}
                onClick={() => {
                  setApplicantCollection(applicantCollection)
                  setApplicantCurrentId(experience.applicant.id)
                }}
              />
            ))}

          {hasNextPage && (
            <Button
              variation={ButtonVariation.Neutral}
              onClick={handleLoadMore}
              disabled={isFetchingNextPage}
              fullWidth
            >
              {isFetchingNextPage
                ? 'Chargement...'
                : 'Charger plus de contrats'}
            </Button>
          )}
        </div>
      </AnimatePresence>
    </DashboardBlockLayout>
  )
}
